// Professional PDF Generation Service for Quiz Results
// Uses html2canvas to capture HTML content and convert to PDF
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Kosha data with colors and descriptions for PDF-specific rendering
const KOSHA_DATA = {
  annamaya: {
    name: "Annamaya Kosha",
    subtitle: "Physical Layer",
    color: "#40A578",
    description: "The physical dimension - touch, presence, shared activities, and bodily connection.",
    highRecommendation: "Continue nurturing your physical connection through shared activities and touch.",
    lowRecommendation: "Prioritize daily rituals of touch - holding hands, embracing, or exercising together."
  },
  pranamaya: {
    name: "Pranamaya Kosha",
    subtitle: "Energy Layer",
    color: "#FF9800",
    description: "The vital energy exchange - life force that flows in shared moments and daily rhythms.",
    highRecommendation: "Your energy sync is remarkable. Keep aligning your daily rhythms.",
    lowRecommendation: "Try breathing exercises together and align your daily routines mindfully."
  },
  manomaya: {
    name: "Manomaya Kosha",
    subtitle: "Mind Layer",
    color: "#E91E63",
    description: "Mental and emotional connection - thoughts, feelings, and communication patterns.",
    highRecommendation: "Your communication flows beautifully. Continue creating safe spaces for sharing.",
    lowRecommendation: "Practice active listening and approach conversations with curiosity, not judgment."
  },
  vijnanamaya: {
    name: "Vijnanamaya Kosha",
    subtitle: "Wisdom Layer",
    color: "#FFC107",
    description: "Intellectual and growth dimension - shared values and aligned life philosophy.",
    highRecommendation: "You're true allies in growth. Continue supporting each other's evolution.",
    lowRecommendation: "Discuss dreams openly and seek alignment in your core values and life vision."
  },
  anandamaya: {
    name: "Anandamaya Kosha",
    subtitle: "Bliss Layer",
    color: "#F44336",
    description: "The spiritual and bliss dimension - deepest joy, unconditional love, and sacred connection.",
    highRecommendation: "Your spiritual bond is transcendent. Nurture this sacred connection.",
    lowRecommendation: "Explore spiritual practices together - meditation, gratitude, or shared silence."
  }
};

const getScoreLabel = (score) => {
  if (score >= 80) return { label: 'Excellent', color: '#4CAF50' };
  if (score >= 60) return { label: 'Good', color: '#8BC34A' };
  if (score >= 40) return { label: 'Fair', color: '#FFC107' };
  return { label: 'Needs Attention', color: '#FF5722' };
};

// A4 dimensions at 96 DPI (standard screen resolution)
const PAGE_WIDTH = 794;  // A4 width in pixels at 96 DPI
const PAGE_HEIGHT = 1123; // A4 height in pixels at 96 DPI

/**
 * Create Page 1: Cover & Overall Score
 */
const createPage1 = (results, userName, quizType) => {
  const overallScore = results.overall;
  const overallLabel = getScoreLabel(overallScore);
  const reportTitle = quizType === 'Life' ? 'Life Transformation' : 'Relationship Harmony';
  const koshaKeys = ['annamaya', 'pranamaya', 'manomaya', 'vijnanamaya', 'anandamaya'];

  const page = document.createElement('div');
  page.className = 'pdf-page';
  page.style.cssText = `
    width: ${PAGE_WIDTH}px;
    height: ${PAGE_HEIGHT}px;
    background: white;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
  `;

  page.innerHTML = `
    <!-- Header -->
    <div style="background: linear-gradient(135deg, #228B22 0%, #006400 100%); padding: 40px; text-align: center;">
      <h1 style="color: white; font-size: 36px; margin: 0; font-weight: bold; letter-spacing: 2px; font-family: Georgia, serif;">SIWON</h1>
      <p style="color: #90EE90; font-size: 14px; margin: 10px 0 0 0; letter-spacing: 3px;">SILENCE TO WONDERMENT</p>
      <p style="color: white; font-size: 18px; margin: 20px 0 0 0;">Your ${reportTitle} Diagnostic Report</p>
    </div>
    
    <!-- Welcome -->
    <div style="padding: 30px 50px; text-align: center;">
      <p style="color: #666; font-size: 16px; margin: 0;">
        Welcome, <strong style="color: #228B22;">${userName}</strong>. Here's your personalized Five Koshas analysis.
      </p>
    </div>
    
    <!-- Overall Score Card -->
    <div style="margin: 0 50px; background: #f8f9fa; border-radius: 15px; padding: 40px; text-align: center;">
      <div style="display: inline-block; background: #7CFC00; color: #006400; padding: 8px 20px; border-radius: 20px; font-size: 12px; font-weight: bold; margin-bottom: 20px;">
        Overall Harmony Score
      </div>
      <div style="font-size: 72px; font-weight: bold; color: ${overallLabel.color}; margin: 10px 0; font-family: Georgia, serif;">
        ${overallScore}%
      </div>
      <p style="color: ${overallLabel.color}; font-size: 18px; font-weight: 600; margin: 10px 0;">${overallLabel.label}</p>
      <p style="color: #666; font-size: 14px; margin-top: 15px; line-height: 1.6;">
        ${overallScore >= 70 
          ? 'Your connection shows beautiful harmony across the Five Koshas. Continue nurturing this bond.' 
          : overallScore >= 50 
          ? 'Your connection has a solid foundation with room for deeper growth and understanding.'
          : 'Your connection is calling for attention and care. Transformation and deeper harmony await.'}
      </p>
    </div>
    
    <!-- Quick Score Summary - Using table for better html2canvas support -->
    <div style="margin: 40px 50px 0 50px;">
      <h3 style="color: #228B22; font-size: 20px; margin-bottom: 20px; font-weight: 600;">Five Koshas Balance</h3>
      <table style="width: 100%; border-collapse: collapse;">
        ${koshaKeys.map(key => {
          const score = results[key];
          const kosha = KOSHA_DATA[key];
          return `
            <tr>
              <td style="width: 130px; padding: 8px 0; font-size: 13px; color: ${kosha.color}; font-weight: 600;">${kosha.subtitle}</td>
              <td style="padding: 8px 15px;">
                <div style="background: #e0e0e0; height: 20px; border-radius: 10px; overflow: hidden; position: relative;">
                  <div style="width: ${score}%; height: 100%; background: ${kosha.color}; border-radius: 10px;"></div>
                </div>
              </td>
              <td style="width: 50px; text-align: right; font-weight: bold; color: #333; padding: 8px 0;">${score}%</td>
            </tr>
          `;
        }).join('')}
      </table>
    </div>
    
    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; width: 100%; background: #228B22; padding: 15px 0; text-align: center;">
      <p style="color: white; font-size: 11px; margin: 0;">www.siwongalaxy.com | siwongalaxy108@gmail.com</p>
      <p style="color: #90EE90; font-size: 10px; margin: 5px 0 0 0;">Page 1 of 4</p>
    </div>
  `;

  return page;
};

/**
 * Create Page 2: First 3 Koshas Detail
 */
const createPage2 = (results) => {
  const koshaKeys = ['annamaya', 'pranamaya', 'manomaya'];

  const page = document.createElement('div');
  page.className = 'pdf-page';
  page.style.cssText = `
    width: ${PAGE_WIDTH}px;
    height: ${PAGE_HEIGHT}px;
    background: white;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
  `;

  page.innerHTML = `
    <!-- Header -->
    <div style="background: #228B22; padding: 20px 40px; text-align: center;">
      <h2 style="color: white; font-size: 20px; margin: 0; font-weight: 600;">Detailed Kosha Analysis</h2>
    </div>
    
    <div style="padding: 30px 50px;">
      ${koshaKeys.map(key => {
        const score = results[key];
        const kosha = KOSHA_DATA[key];
        const isStrength = score >= 60;
        return `
          <div style="background: #f8f9fa; border-radius: 12px; padding: 25px; margin-bottom: 25px; border-left: 5px solid ${kosha.color};">
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
              <tr>
                <td style="vertical-align: top;">
                  <h3 style="color: ${kosha.color}; font-size: 18px; margin: 0; font-weight: 700;">${kosha.name}</h3>
                  <p style="color: #888; font-size: 12px; margin: 5px 0 0 0;">${kosha.subtitle}</p>
                </td>
                <td style="text-align: right; vertical-align: top;">
                  <span style="display: inline-block; background: ${isStrength ? '#4CAF50' : '#FF9800'}; color: white; padding: 8px 16px; border-radius: 20px; font-weight: bold; font-size: 16px;">
                    ${score}%
                  </span>
                </td>
              </tr>
            </table>
            <p style="color: #555; font-size: 13px; line-height: 1.6; margin: 0 0 15px 0;">${kosha.description}</p>
            <div style="background: #e8f5e9; padding: 12px; border-radius: 8px;">
              <p style="color: #2E7D32; font-size: 12px; margin: 0; line-height: 1.5;">
                <strong>Recommendation:</strong> ${isStrength ? kosha.highRecommendation : kosha.lowRecommendation}
              </p>
            </div>
          </div>
        `;
      }).join('')}
    </div>
    
    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; width: 100%; background: #228B22; padding: 15px 0; text-align: center;">
      <p style="color: white; font-size: 11px; margin: 0;">"Your Inner SILENCE unfolds WONDERMENT in Life"</p>
      <p style="color: #90EE90; font-size: 10px; margin: 5px 0 0 0;">Page 2 of 4</p>
    </div>
  `;

  return page;
};

/**
 * Create Page 3: Last 2 Koshas + Key Insights
 */
const createPage3 = (results) => {
  const koshaKeys = ['vijnanamaya', 'anandamaya'];
  const allKoshaKeys = ['annamaya', 'pranamaya', 'manomaya', 'vijnanamaya', 'anandamaya'];
  const strengths = allKoshaKeys.filter(k => results[k] >= 60);
  const opportunities = allKoshaKeys.filter(k => results[k] < 60);

  const page = document.createElement('div');
  page.className = 'pdf-page';
  page.style.cssText = `
    width: ${PAGE_WIDTH}px;
    height: ${PAGE_HEIGHT}px;
    background: white;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
  `;

  page.innerHTML = `
    <!-- Header -->
    <div style="background: #228B22; padding: 20px 40px; text-align: center;">
      <h2 style="color: white; font-size: 20px; margin: 0; font-weight: 600;">Detailed Kosha Analysis (Continued)</h2>
    </div>
    
    <div style="padding: 30px 50px;">
      ${koshaKeys.map(key => {
        const score = results[key];
        const kosha = KOSHA_DATA[key];
        const isStrength = score >= 60;
        return `
          <div style="background: #f8f9fa; border-radius: 12px; padding: 25px; margin-bottom: 25px; border-left: 5px solid ${kosha.color};">
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
              <tr>
                <td style="vertical-align: top;">
                  <h3 style="color: ${kosha.color}; font-size: 18px; margin: 0; font-weight: 700;">${kosha.name}</h3>
                  <p style="color: #888; font-size: 12px; margin: 5px 0 0 0;">${kosha.subtitle}</p>
                </td>
                <td style="text-align: right; vertical-align: top;">
                  <span style="display: inline-block; background: ${isStrength ? '#4CAF50' : '#FF9800'}; color: white; padding: 8px 16px; border-radius: 20px; font-weight: bold; font-size: 16px;">
                    ${score}%
                  </span>
                </td>
              </tr>
            </table>
            <p style="color: #555; font-size: 13px; line-height: 1.6; margin: 0 0 15px 0;">${kosha.description}</p>
            <div style="background: #e8f5e9; padding: 12px; border-radius: 8px;">
              <p style="color: #2E7D32; font-size: 12px; margin: 0; line-height: 1.5;">
                <strong>Recommendation:</strong> ${isStrength ? kosha.highRecommendation : kosha.lowRecommendation}
              </p>
            </div>
          </div>
        `;
      }).join('')}
      
      <!-- Key Insights -->
      <div style="margin-top: 30px;">
        <h3 style="color: #228B22; font-size: 18px; margin-bottom: 20px; font-weight: 600;">Key Insights</h3>
        
        ${strengths.length > 0 ? `
          <div style="background: #e8f5e9; border-radius: 10px; padding: 15px; margin-bottom: 15px;">
            <p style="color: #4CAF50; font-weight: bold; margin: 0 0 10px 0;">Your Strengths:</p>
            ${strengths.map(k => `<p style="color: #333; font-size: 13px; margin: 5px 0 5px 15px;">• ${KOSHA_DATA[k].name}: ${results[k]}%</p>`).join('')}
          </div>
        ` : ''}
        
        ${opportunities.length > 0 ? `
          <div style="background: #fff3e0; border-radius: 10px; padding: 15px;">
            <p style="color: #FF9800; font-weight: bold; margin: 0 0 10px 0;">Areas for Growth:</p>
            ${opportunities.map(k => `<p style="color: #333; font-size: 13px; margin: 5px 0 5px 15px;">• ${KOSHA_DATA[k].name}: ${results[k]}%</p>`).join('')}
          </div>
        ` : ''}
      </div>
    </div>
    
    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; width: 100%; background: #228B22; padding: 15px 0; text-align: center;">
      <p style="color: white; font-size: 11px; margin: 0;">"Your Inner SILENCE unfolds WONDERMENT in Life"</p>
      <p style="color: #90EE90; font-size: 10px; margin: 5px 0 0 0;">Page 3 of 4</p>
    </div>
  `;

  return page;
};

/**
 * Create Page 4: Call to Action
 */
const createPage4 = () => {
  const generatedDate = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

  const page = document.createElement('div');
  page.className = 'pdf-page';
  page.style.cssText = `
    width: ${PAGE_WIDTH}px;
    height: ${PAGE_HEIGHT}px;
    background: white;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
  `;

  page.innerHTML = `
    <!-- Header -->
    <div style="background: #228B22; padding: 20px 40px; text-align: center;">
      <h2 style="color: white; font-size: 20px; margin: 0; font-weight: 600;">Your Path Forward</h2>
    </div>
    
    <div style="padding: 40px 50px;">
      <!-- Main CTA Text -->
      <div style="text-align: center; margin-bottom: 40px;">
        <h3 style="color: #228B22; font-size: 24px; margin: 0 0 15px 0; font-weight: 600; font-family: Georgia, serif;">Ready for Deeper Transformation?</h3>
        <p style="color: #666; font-size: 14px; line-height: 1.6;">
          Your diagnostic reveals unique patterns. Connect with Vinod Kumar<br/>
          for personalized coaching to harmonize your life across all Five Koshas.
        </p>
      </div>
      
      <!-- CTA Boxes - Using table for better html2canvas support -->
      <table style="width: 100%; border-collapse: separate; border-spacing: 20px 0; margin-bottom: 40px;">
        <tr>
          <td style="width: 50%; background: #f8f9fa; border-radius: 12px; padding: 25px; text-align: center; vertical-align: top;">
            <h4 style="color: #228B22; font-size: 16px; margin: 0 0 10px 0; font-weight: 600;">Book a Free Discovery Call</h4>
            <p style="color: #666; font-size: 13px; margin: 0 0 15px 0;">30-minute personalized session to discuss your results</p>
            <span style="display: inline-block; background: #7CFC00; color: #006400; padding: 10px 25px; border-radius: 20px; font-weight: bold; font-size: 12px;">
              Schedule Now
            </span>
          </td>
          <td style="width: 50%; background: #f8f9fa; border-radius: 12px; padding: 25px; text-align: center; vertical-align: top;">
            <h4 style="color: #228B22; font-size: 16px; margin: 0 0 10px 0; font-weight: 600;">Join Our Free Webinar</h4>
            <p style="color: #666; font-size: 13px; margin: 0 0 15px 0;">Learn the SIWON methodology and transform your life</p>
            <span style="display: inline-block; border: 2px solid #228B22; color: #228B22; padding: 10px 25px; border-radius: 20px; font-weight: bold; font-size: 12px;">
              Register Free
            </span>
          </td>
        </tr>
      </table>
      
      <!-- WhatsApp Community -->
      <div style="background: #e8f5e9; border-radius: 12px; padding: 25px; text-align: center; margin-bottom: 40px;">
        <h4 style="color: #228B22; font-size: 16px; margin: 0 0 10px 0; font-weight: 600;">Join Our WhatsApp Community</h4>
        <p style="color: #666; font-size: 13px; margin: 0;">
          Connect with like-minded seekers on the path to transformation
        </p>
      </div>
      
      <!-- Contact Information -->
      <div style="text-align: center;">
        <h4 style="color: #333; font-size: 16px; margin: 0 0 15px 0; font-weight: 600;">Connect With Us</h4>
        <p style="color: #666; font-size: 13px; margin: 5px 0;">Website: www.siwongalaxy.com</p>
        <p style="color: #666; font-size: 13px; margin: 5px 0;">Email: siwongalaxy108@gmail.com</p>
        <p style="color: #666; font-size: 13px; margin: 5px 0;">Phone: +91 7042106100</p>
        <p style="color: #666; font-size: 13px; margin: 5px 0;">Location: Gurgaon, India</p>
      </div>
    </div>
    
    <!-- Footer -->
    <div style="position: absolute; bottom: 0; left: 0; right: 0; width: 100%; background: #228B22; padding: 20px 0; text-align: center;">
      <p style="color: white; font-size: 14px; font-style: italic; margin: 0 0 10px 0;">"Your Inner SILENCE unfolds WONDERMENT in Life"</p>
      <p style="color: #90EE90; font-size: 11px; margin: 0;">
        Report generated on ${generatedDate}
      </p>
      <p style="color: #90EE90; font-size: 10px; margin: 5px 0 0 0;">Page 4 of 4</p>
    </div>
  `;

  return page;
};

/**
 * Generate comprehensive 4-page PDF report
 * @param {Object} results - Quiz results with kosha scores
 * @param {string} userName - User's name
 * @param {string} quizType - "Relationship" or "Life"
 */
export const generateResultsPDF = async (results, userName, quizType = "Relationship") => {
  try {
    // Create a visible container (html2canvas works better with visible elements)
    const container = document.createElement('div');
    container.id = 'pdf-render-container';
    container.style.cssText = `
      position: fixed;
      left: 0;
      top: 0;
      z-index: -9999;
      background: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    `;
    
    // Create all pages
    const page1 = createPage1(results, userName, quizType);
    const page2 = createPage2(results);
    const page3 = createPage3(results);
    const page4 = createPage4();
    
    container.appendChild(page1);
    container.appendChild(page2);
    container.appendChild(page3);
    container.appendChild(page4);
    
    document.body.appendChild(container);
    
    // Wait for DOM to be fully rendered
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    
    // Get all pages
    const pages = container.querySelectorAll('.pdf-page');
    
    for (let i = 0; i < pages.length; i++) {
      const pageElement = pages[i];
      
      // Render page to canvas with optimal settings
      const canvas = await html2canvas(pageElement, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        windowWidth: PAGE_WIDTH,
        windowHeight: PAGE_HEIGHT,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });
      
      // Add page to PDF
      if (i > 0) {
        pdf.addPage();
      }
      
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
    }
    
    // Clean up
    document.body.removeChild(container);
    
    // Save the PDF
    const sanitizedName = userName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    const fileName = `Siwon_Galaxy_${quizType}_Report_${sanitizedName}.pdf`;
    pdf.save(fileName);
    
    console.log('PDF generated successfully:', fileName);
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

export default generateResultsPDF;
